import { useApolloClient } from "@apollo/client";
import { normalizeSmartProcess } from "@msys/common";
import { SearchInput } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslate } from "@tolgee/react";
import {
  namedOperations,
  OfferedProjectType,
} from "../../../clients/graphqlTypes.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { removeSubcategoriesOfDeletedParent } from "../../features/categories/helpers.js";
import { useCategories } from "../../features/categories/useCategories.js";
import {
  buildTaxonomyIndex,
  type CatalogueBuffer,
  expandedNonLeafIds,
  matrixCellKey,
  parseMatrixCellKey,
} from "../../features/organisations/offeredServices/catalogueModel.js";
import { CatalogueGrid } from "../../features/organisations/offeredServices/CatalogueGrid.js";
import {
  type OverrideDraft,
  overrideHasValue,
  OverridePopover,
} from "../../features/organisations/offeredServices/OverridePopover.js";
import { useOverridePopover_TemplatesByIdsQuery } from "../../features/organisations/offeredServices/OverridePopover.generated.js";
import {
  AUTO_FILLED_PROJECT_TYPES,
  OFFERED_PROJECT_TYPES,
} from "../../features/organisations/offeredServices/projectTypes.js";
import { useProjectTypes } from "../../features/projects/useProjectTypes.js";
import {
  useOrganisationSettingsOfferedServicesQuery,
  useReplaceOrganisationCategoryProjectTypeMappingOverridesMutation,
  useSetOrganisationOfferedServicesMutation,
} from "./OrganisationSettingsOfferedServices.generated.js";

// View mode reuses the edit-mode CatalogueGrid in read-only form; its
// interaction callbacks are required props but never fire when disabled.
const NOOP = () => {};

const overriddenCellsOf = (
  overrides: ReadonlyMap<string, OverrideDraft>
): Set<string> => {
  const cells = new Set<string>();
  for (const [key, draft] of overrides) {
    if (overrideHasValue(draft)) cells.add(key);
  }
  return cells;
};

function groupByKey<T, K, V>(
  items: ReadonlyArray<T>,
  getKey: (item: T) => K,
  getValue: (item: T) => V
): Map<K, Set<V>> {
  const map = new Map<K, Set<V>>();
  for (const item of items) {
    const key = getKey(item);
    const value = getValue(item);
    const existing = map.get(key);
    if (existing) existing.add(value);
    else map.set(key, new Set([value]));
  }
  return map;
}

interface Props {
  submenuItems: PageTopbarItem[];
}

export const OrganisationSettingsOfferedServices = ({
  submenuItems,
}: Props) => {
  const { t } = useTranslate([
    "OrganisationPageTopbar",
    "OrganisationProfile",
    "Global",
  ]);
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useOrganisationSettingsOfferedServicesQuery({
    client,
    fetchPolicy: "cache-and-network",
  });
  const organisation = query.data?.viewer?.organisation;

  const [setServices, { loading: savingServices }] =
    useSetOrganisationOfferedServicesMutation({ client });
  const [replaceOverrides, { loading: savingOverrides }] =
    useReplaceOrganisationCategoryProjectTypeMappingOverridesMutation({
      client,
    });
  const saving = savingServices || savingOverrides;

  const { categories: allCategories } = useCategories();
  const { projectTypeLabels } = useProjectTypes();

  // Persisted state seeded from the query, shaped for the CatalogueGrid. The
  // read-only view buffer, `enterEdit`, and the `isDirty` baseline all derive
  // from this single source rather than re-deriving from `organisation`.
  const persisted = React.useMemo(() => {
    const declared = new Set(organisation?.categories ?? []);
    const selections = new Set<string>();
    for (const s of organisation?.offeredServices ?? []) {
      selections.add(matrixCellKey(s.categoryId, s.projectType));
    }
    const overrides = new Map<string, OverrideDraft>();
    for (const o of organisation?.categoryProjectTypeMappingOverrides ?? []) {
      overrides.set(matrixCellKey(o.categoryId, o.projectType), {
        defaultTemplateId: o.defaultTemplateId ?? null,
        defaultSmartProcess: o.defaultSmartProcess ?? null,
      });
    }
    return { declared, selections, overrides };
  }, [
    organisation?.categories,
    organisation?.offeredServices,
    organisation?.categoryProjectTypeMappingOverrides,
  ]);

  const declaredIds = persisted.declared;

  const [mode, setMode] = React.useState<"view" | "edit">("view");
  const [buffer, setBuffer] = React.useState<CatalogueBuffer>({
    declared: new Set(),
    selections: new Set(),
  });
  const [overrideBuffer, setOverrideBuffer] = React.useState<
    Map<string, OverrideDraft>
  >(new Map());
  const [editingOverrideCell, setEditingOverrideCell] = React.useState<{
    categoryId: string;
    projectType: OfferedProjectType;
    anchorEl: HTMLElement;
  } | null>(null);
  const [confirmRemovalOpen, setConfirmRemovalOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  // Excludes the "__deleted__" sentinel subtree so the cascade index and the
  // grid's rows agree — otherwise select-all would declare hidden categories.
  const usableCategories = React.useMemo(
    () => removeSubcategoriesOfDeletedParent(allCategories, allCategories),
    [allCategories]
  );

  const index = React.useMemo(
    () => buildTaxonomyIndex(usableCategories),
    [usableCategories]
  );

  const projectTypesByCategory = React.useMemo(
    () =>
      groupByKey(
        organisation?.offeredServices ?? [],
        o => o.categoryId,
        o => o.projectType
      ),
    [organisation?.offeredServices]
  );

  // Read-only view buffer: the persisted declared categories + offered
  // services, shaped for the disabled CatalogueGrid. Editing seeds its own
  // buffer separately in `enterEdit`.
  const viewBuffer = React.useMemo<CatalogueBuffer>(
    () => ({ declared: persisted.declared, selections: persisted.selections }),
    [persisted]
  );

  const viewOverriddenCells = React.useMemo(
    () => overriddenCellsOf(persisted.overrides),
    [persisted]
  );

  // Expand every non-leaf category with a declared descendant so the persisted
  // selections are visible on entry. Derived from persisted data, so it stays
  // stable across the view/edit toggle (edits don't reset manual expansion).
  const initiallyExpandedIds = React.useMemo(
    () => expandedNonLeafIds(declaredIds, index),
    [index, declaredIds]
  );

  const enterEdit = () => {
    setBuffer({
      declared: new Set(persisted.declared),
      selections: new Set(persisted.selections),
    });
    setOverrideBuffer(new Map(persisted.overrides));
    setMode("edit");
  };

  // Discard: drop the buffer and return to view mode.
  const cancelEdit = () => setMode("view");

  const overriddenCells = React.useMemo(
    () => overriddenCellsOf(overrideBuffer),
    [overrideBuffer]
  );

  // Resolve the titles of every template referenced by an override (persisted or
  // buffered) in one query, so the grid can label each overridden chip with its
  // template name. Smart-process-only overrides carry no template id and fall
  // back to the "Custom" chip label in the grid.
  const overrideTemplateIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const d of persisted.overrides.values()) {
      if (d.defaultTemplateId) ids.add(d.defaultTemplateId);
    }
    for (const d of overrideBuffer.values()) {
      if (d.defaultTemplateId) ids.add(d.defaultTemplateId);
    }
    return [...ids];
  }, [persisted.overrides, overrideBuffer]);

  const templatesQuery = useOverridePopover_TemplatesByIdsQuery({
    client,
    variables: { templateIds: overrideTemplateIds },
    skip: overrideTemplateIds.length === 0,
  });

  const templateTitleById = React.useMemo(() => {
    const byId = new Map<string, string>();
    const conn = templatesQuery.data?.searchTemplates;
    if (conn?.__typename === "QuoteTemplateConnection") {
      for (const edge of conn.edges) byId.set(edge.node.id, edge.node.title);
    }
    return byId;
  }, [templatesQuery.data]);

  const overrideLabelsOf = React.useCallback(
    (overrides: ReadonlyMap<string, OverrideDraft>): Map<string, string> => {
      const labels = new Map<string, string>();
      for (const [key, draft] of overrides) {
        if (!overrideHasValue(draft)) continue;
        if (!draft.defaultTemplateId) continue;
        const title = templateTitleById.get(draft.defaultTemplateId);
        if (title) labels.set(key, title);
      }
      return labels;
    },
    [templateTitleById]
  );

  const viewOverrideLabels = React.useMemo(
    () => overrideLabelsOf(persisted.overrides),
    [overrideLabelsOf, persisted.overrides]
  );
  const editOverrideLabels = React.useMemo(
    () => overrideLabelsOf(overrideBuffer),
    [overrideLabelsOf, overrideBuffer]
  );

  const handleEditOverride = React.useCallback(
    (
      categoryId: string,
      projectType: OfferedProjectType,
      anchorEl: HTMLElement
    ) => {
      setEditingOverrideCell({ categoryId, projectType, anchorEl });
    },
    []
  );

  const handleOverrideDraftChange = (next: OverrideDraft) => {
    if (!editingOverrideCell) return;
    const key = matrixCellKey(
      editingOverrideCell.categoryId,
      editingOverrideCell.projectType
    );
    setOverrideBuffer(prev => {
      const copy = new Map(prev);
      copy.set(key, next);
      return copy;
    });
  };

  // Services persisted on save: every selected cell on a still-declared **leaf**
  // category. Offered services are leaf-only by invariant — the grid never
  // creates non-leaf action rows, and any pre-existing non-leaf rows (from the
  // legacy flat matrix, M2M sync, or admin tooling) are intentionally dropped so
  // the table converges to leaf-only. `isDirty` reuses this, so a legacy org
  // that still has non-leaf rows opens as dirty rather than being silently
  // rewritten on an otherwise-untouched save.
  const activeServices = React.useMemo(() => {
    const services: { categoryId: string; projectType: OfferedProjectType }[] =
      [];
    for (const key of buffer.selections) {
      const { categoryId, projectType } = parseMatrixCellKey(key);
      if (buffer.declared.has(categoryId) && index.isLeaf(categoryId)) {
        services.push({ categoryId, projectType });
      }
    }
    return services;
  }, [buffer, index]);

  const isDirty = React.useMemo(() => {
    const setEq = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
      a.size === b.size && [...a].every(x => b.has(x));

    // Compare what would actually be saved (declared ids still in the taxonomy)
    // against the persisted set, mirroring executeSave's filter — otherwise an
    // org whose only divergence is a stale declared id never opens as dirty and
    // the cleanup can never run.
    const nextDeclared = new Set(
      [...buffer.declared].filter(id => index.has(id))
    );
    if (!setEq(nextDeclared, persisted.declared)) return true;

    const loadedServiceKeys = persisted.selections;
    const nextServiceKeys = new Set(
      activeServices.map(s => matrixCellKey(s.categoryId, s.projectType))
    );
    if (!setEq(loadedServiceKeys, nextServiceKeys)) return true;

    const normOverride = (d: OverrideDraft | undefined) =>
      `${d?.defaultTemplateId ?? ""}|${normalizeSmartProcess(d?.defaultSmartProcess) ?? ""}`;
    const loadedOverrides = persisted.overrides;
    // Only override cells that survive as active-leaf services matter on save.
    const effectiveKeys = new Set(
      [...overrideBuffer.keys()].filter(k => nextServiceKeys.has(k))
    );
    const loadedKeys = new Set(
      [...loadedOverrides.keys()].filter(k => loadedServiceKeys.has(k))
    );
    const allKeys = new Set([...effectiveKeys, ...loadedKeys]);
    for (const k of allKeys) {
      if (
        normOverride(overrideBuffer.get(k)) !==
        normOverride(loadedOverrides.get(k))
      ) {
        return true;
      }
    }
    return false;
  }, [buffer, persisted, overrideBuffer, activeServices, index]);

  // Removed categories whose project-type selections would be lost on save.
  // The user is asked to confirm before committing those losses.
  const removedWithServicesNames = React.useMemo(() => {
    if (!organisation) return [] as string[];
    if (mode !== "edit") return [] as string[];
    const byId = new Map(allCategories.map(c => [c.id, c]));
    const removedIds = Array.from(declaredIds).filter(
      id => !buffer.declared.has(id)
    );
    const removedWithTypes = removedIds.filter(
      id => (projectTypesByCategory.get(id)?.size ?? 0) > 0
    );
    return removedWithTypes.map(id => byId.get(id)?.name ?? id);
  }, [
    organisation,
    mode,
    declaredIds,
    buffer,
    projectTypesByCategory,
    allCategories,
  ]);

  const executeSave = async () => {
    if (!organisation) return;
    try {
      // Drop ids no longer in the taxonomy (e.g. since-deleted categories still
      // present in persisted org data) so a stale declaration isn't re-saved.
      const categories = Array.from(buffer.declared).filter(id =>
        index.has(id)
      );
      const services = activeServices;
      const serviceKeys = new Set(
        services.map(s => matrixCellKey(s.categoryId, s.projectType))
      );
      // Overrides are FK-bound to offered services, so keep only those whose
      // cell survives as an active-leaf service and that carry a value.
      const overrides = [];
      for (const [key, draft] of overrideBuffer) {
        if (!serviceKeys.has(key)) continue;
        if (!overrideHasValue(draft)) continue;
        const { categoryId, projectType } = parseMatrixCellKey(key);
        overrides.push({
          categoryId,
          projectType,
          defaultTemplateId: draft.defaultTemplateId,
          defaultSmartProcess: normalizeSmartProcess(draft.defaultSmartProcess),
        });
      }
      // Step 1 must precede step 2: overrides reference offered_services rows.
      await setServices({ variables: { input: { categories, services } } });
      await replaceOverrides({
        variables: { input: { organisationId: organisation.id, overrides } },
      });
      await client.refetchQueries({
        include: [namedOperations.Query.OrganisationSettingsOfferedServices],
      });
      setMode("view");
    } catch (error) {
      // Step 1 may have committed (FK-cascade-deleting override rows) even when
      // step 2 fails, so resync the cache before surfacing the error; the buffer
      // is left intact so the user can retry.
      await client.refetchQueries({
        include: [namedOperations.Query.OrganisationSettingsOfferedServices],
      });
      const message =
        error instanceof Error
          ? error.message
          : t("Something went wrong. Please try again.", { ns: "Global" });
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleSave = () => {
    if (removedWithServicesNames.length > 0) {
      setConfirmRemovalOpen(true);
    } else {
      void executeSave();
    }
  };

  return (
    <Page
      title={t("Offered services", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
    >
      {organisation && (
        <PageContainer $noPadding>
          <Stack spacing={2}>
            <Box
              sx={{
                // Full-bleed background pins to the top and covers the (removed)
                // container padding so no scrolling row peeks above or beside
                // the toolbar. The inner row is inset like the grid so its
                // bottom border lines up with the grid, not the viewport edge.
                position: "sticky",
                top: 0,
                zIndex: 2,
                px: 2,
                pt: 2,
                backgroundColor: "background.default",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  pb: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <SearchInput
                  placeholder={t("Search categories", {
                    ns: "OrganisationProfile",
                  })}
                  value={search}
                  onChangeSearchTerm={setSearch}
                  size="extra-small"
                  sx={{
                    transition: "ease-in-out 250ms max-width",
                    maxWidth: "100%",
                    ":not(:focus-within)": { maxWidth: 320 },
                  }}
                />
                <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                  {mode === "view" ? (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={enterEdit}
                    >
                      {t("Edit", { ns: "Global" })}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        onClick={cancelEdit}
                        disabled={saving}
                      >
                        {t("Discard", { ns: "Global" })}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                      >
                        {t("Save", { ns: "Global" })}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
              {mode === "view" && (
                <CatalogueGrid
                  readOnly
                  categories={usableCategories}
                  index={index}
                  buffer={viewBuffer}
                  search={search}
                  projectTypes={OFFERED_PROJECT_TYPES}
                  onBufferChange={NOOP}
                  overriddenCells={viewOverriddenCells}
                  overrideLabels={viewOverrideLabels}
                  onEditOverride={NOOP}
                  initiallyExpandedIds={initiallyExpandedIds}
                />
              )}

              {mode === "edit" && (
                <CatalogueGrid
                  categories={usableCategories}
                  index={index}
                  buffer={buffer}
                  search={search}
                  projectTypes={OFFERED_PROJECT_TYPES}
                  autoFillProjectTypes={AUTO_FILLED_PROJECT_TYPES}
                  onBufferChange={setBuffer}
                  overriddenCells={overriddenCells}
                  overrideLabels={editOverrideLabels}
                  onEditOverride={handleEditOverride}
                  initiallyExpandedIds={initiallyExpandedIds}
                />
              )}
            </Box>
          </Stack>
        </PageContainer>
      )}
      {mode === "edit" && editingOverrideCell && (
        <OverridePopover
          anchorEl={editingOverrideCell.anchorEl}
          projectTypeLabel={projectTypeLabels[editingOverrideCell.projectType]}
          draft={
            overrideBuffer.get(
              matrixCellKey(
                editingOverrideCell.categoryId,
                editingOverrideCell.projectType
              )
            ) ?? { defaultTemplateId: null, defaultSmartProcess: null }
          }
          onChange={handleOverrideDraftChange}
          onClose={() => setEditingOverrideCell(null)}
        />
      )}
      {confirmRemovalOpen && (
        <ConfirmModal
          title={t("Remove category", { ns: "OrganisationProfile" })}
          text={t(
            "Removing {categories} will also remove all project-type selections within them. Continue?",
            {
              ns: "OrganisationProfile",
              categories: removedWithServicesNames.join(", "),
            }
          )}
          confirmButtonLabel={t("Remove", { ns: "Global" })}
          handleConfirm={executeSave}
          handleClose={() => setConfirmRemovalOpen(false)}
        />
      )}
    </Page>
  );
};
